/**
 * LineTakes — every take of one line, and restoring one instead of paying to re-render it.
 *
 * ⭐ WHY THERE IS ANYTHING TO RESTORE. `LineRenderer.renderLine` SUPERSEDES rather than deletes: an
 * earlier take keeps its node, its s3Key and its audio, marked `isActive: false`. So the audio that
 * spoke a line's original words survives an editorial correction or a re-render — and until these
 * endpoints nothing could reach it, which made undo cost a second ElevenLabs charge for a file
 * already sitting in S3.
 *
 * ⛔ RESTORING DOES NOT RE-STITCH. The chapter's stitch still contains whatever was active when it was
 * made, so restoring makes that stitch stale — the Stitch gate says so (`stitch-stale`), and this
 * panel says it in the confirmation. An endpoint that quietly re-stitched would spend minutes of work
 * on a decision the operator did not make.
 *
 * ⚠️ EVERY TAKE IS PLAYABLE, because choosing between two takes blind is guessing. The list is the
 * audition; the verdict beside it is only a hint.
 *
 * See docs/journal/EDITORIAL_TEXT_OVERLAY_SPEC.md §3a-b.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon, Label, Message, Popup } from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';
import ConfirmModal from '../prototype/ConfirmModal';

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtDur = (ms) => (ms ? `${Math.round(ms / 1000)}s` : '—');
const fmtKb = (b) => (b ? `${Math.round(b / 1024)} KB` : '—');

/** What the stored spoken-text hash can and cannot tell us. */
const VERDICT = {
  current: { text: 'current', color: 'green', hint: 'This take spoke the words the line would say if rendered now.' },
  differs: { text: 'different words', color: 'orange', hint: 'This take spoke something else — an older text, or before a correction was made.' },
  unknown: { text: 'unknown', color: 'grey', hint: 'Rendered before the app recorded what was spoken, so what it says cannot be told from here. Play it.' },
};

const LineTakes = ({ bookId, lineId, onRestored }) => {
  const [takes, setTakes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await apiClient.get(`/voice-rendering/${bookId}/lines/${lineId}/takes`);
      const data = r.data?.data || r.data || {};
      setTakes(data.takes || []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.response?.data?.error || 'Could not read the takes');
    }
  }, [bookId, lineId]);

  useEffect(() => { load(); }, [load]);

  // One element, reused. Two takes playing at once is never what anybody wants.
  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  const play = (take) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playing === take.uuid) { setPlaying(null); return; }
    if (!take.signedUrl) { setError('That take has no playable URL.'); return; }
    const el = new Audio(take.signedUrl);
    el.onended = () => setPlaying(null);
    el.onerror = () => { setError('That take would not play — its link may have expired. Reopen the list.'); setPlaying(null); };
    audioRef.current = el;
    setPlaying(take.uuid);
    el.play().catch(() => { setError('That take would not play.'); setPlaying(null); });
  };

  const restore = async () => {
    const take = confirm;
    setConfirm(null); setBusy(true); setError(null); setMsg(null);
    try {
      const r = await apiClient.post(`/voice-rendering/${bookId}/lines/${lineId}/takes/activate`,
        { renderedUuid: take.uuid });
      const data = r.data?.data || r.data || {};
      setTakes(data.takes || []);
      setMsg(data.note || 'Restored. Re-stitch the chapter to hear it there.');
      if (onRestored) onRestored({ lineId, renderedUuid: take.uuid });
    } catch (e) {
      setError(e.response?.data?.error?.message || e.response?.data?.error || 'Could not restore that take');
    } finally { setBusy(false); }
  };

  if (!takes) return <div style={{ padding: '0.4em 0', color: '#999', fontSize: '0.85em' }}>Reading takes…</div>;

  return (
    <div style={{ padding: '0.35em 0 0.6em 0', fontSize: '0.85em' }}>
      {error && <Message negative size="tiny" onDismiss={() => setError(null)}>{error}</Message>}
      {msg && <Message positive size="tiny" onDismiss={() => setMsg(null)}>{msg}</Message>}
      {takes.length <= 1 && !error && (
        <div style={{ color: '#999' }}>Only one take — nothing earlier to restore.</div>
      )}
      {takes.map((t) => {
        const v = VERDICT[t.textVerdict] || VERDICT.unknown;
        return (
          <div key={t.uuid}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5em', padding: '0.25em 0',
              borderTop: '1px solid #f0f0f0',
            }}>
            <Button size="mini" basic compact icon={playing === t.uuid ? 'pause' : 'play'}
              disabled={!t.signedUrl} onClick={() => play(t)} title="Listen to this take" />
            {t.isActive
              ? <Label size="mini" color="blue">in use</Label>
              : (
                <Button size="mini" basic compact disabled={busy} onClick={() => setConfirm(t)}>
                  make active
                </Button>
              )}
            <span style={{ color: '#666' }}>{fmtWhen(t.renderedAt)}</span>
            <span style={{ color: '#999' }}>{fmtDur(t.durationMs)} · {fmtKb(t.audioBytes)}</span>
            <Popup size="tiny" content={v.hint}
              trigger={<Label size="mini" basic color={v.color}>{v.text}</Label>} />
            {/* A take that was restored says so: its render date is older than its time in use, which
                otherwise reads as a table sorted wrongly. */}
            {t.restoredAt && (
              <Popup size="tiny" content={`Restored ${fmtWhen(t.restoredAt)}`}
                trigger={<Icon name="undo" style={{ color: '#aaa' }} />} />
            )}
          </div>
        );
      })}

      <ConfirmModal
        open={!!confirm}
        title="Use this earlier take?"
        icon="undo"
        danger={false}
        items={confirm ? [`Rendered ${fmtWhen(confirm.renderedAt)} · ${fmtDur(confirm.durationMs)} · ${VERDICT[confirm.textVerdict]?.text || 'unknown'}`] : []}
        note="No audio is generated and nothing is deleted — the take in use now becomes the superseded one, and can be restored the same way. ⚠️ The chapter's stitch still contains the current take, so re-stitch to hear this one in the chapter."
        confirmLabel="Make active"
        loading={busy}
        onConfirm={restore}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default LineTakes;
